import React, { useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { 
  XMarkIcon,
  ClockIcon,
  UserIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import api from '@/services/api'

interface User {
  id: number
  email: string
  fullName: string
  employeeNumber?: string
}

interface AuditLogEntry {
  id: number
  action: string
  details: any
  ipAddress: string
  createdAt: string
  adminUser: {
    firstName: string
    lastName: string
    email: string
  }
  targetUser?: {
    firstName: string
    lastName: string
    email: string
  }
}

interface AuditHistoryResponse {
  auditLogs: AuditLogEntry[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

interface UserAuditModalProps {
  isOpen: boolean
  onClose: () => void
  user: User | null
}

const ACTION_LABELS: Record<string, string> = {
  'PASSWORD_RESET': 'Password Reset',
  'CREDENTIALS_VIEWED': 'Credentials Viewed',
  'USER_ACTIVATED': 'Account Activated',
  'USER_DEACTIVATED': 'Account Deactivated',
  'ROLE_UPDATED': 'Role Updated',
  'FORCE_PASSWORD_RESET': 'Force Password Reset',
  'BULK_USER_ACTIVATED': 'Bulk Activation',
  'BULK_USER_DEACTIVATED': 'Bulk Deactivation',
  'BULK_FORCE_PASSWORD_RESET': 'Bulk Force Password Reset',
  'VIEW_USER_AUDIT_HISTORY': 'Audit History Viewed',
  'REVOKE_PASSWORD_RESET_TOKENS': 'Password Reset Tokens Revoked'
}

const UserAuditModal: React.FC<UserAuditModalProps> = ({
  isOpen,
  onClose,
  user
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  const { data: auditData, isLoading, error } = useQuery<AuditHistoryResponse | null>({
    queryKey: ['user-audit-history', user?.id, currentPage],
    queryFn: async () => {
      if (!user) return null
      const response = await api.get(`/admin/users/${user.id}/audit-history?page=${currentPage}&limit=${pageSize}`)
      return response.data.data as AuditHistoryResponse
    },
    enabled: isOpen && !!user,
    placeholderData: keepPreviousData
  })

  if (!isOpen || !user) return null

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  const getActionColor = (action: string) => {
    switch (action) {
      case 'PASSWORD_RESET':
      case 'FORCE_PASSWORD_RESET':
        return 'text-yellow-600 bg-yellow-100'
      case 'USER_ACTIVATED':
        return 'text-green-600 bg-green-100'
      case 'USER_DEACTIVATED':
        return 'text-red-600 bg-red-100'
      case 'ROLE_UPDATED':
        return 'text-blue-600 bg-blue-100'
      case 'CREDENTIALS_VIEWED':
        return 'text-purple-600 bg-purple-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const renderActionDetails = (entry: AuditLogEntry) => {
    const details = entry.details || {}
    
    switch (entry.action) {
      case 'ROLE_UPDATED':
        return (
          <div className="text-xs text-gray-500 mt-1">
            From: {details.oldRole} → To: {details.newRole}
          </div>
        )
      case 'PASSWORD_RESET':
      case 'CREDENTIALS_VIEWED':
        return details.employeeNumber && (
          <div className="text-xs text-gray-500 mt-1">
            Employee: {details.employeeNumber}
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                <ClockIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  User Activity History
                </h3>
                <p className="text-sm text-gray-500">
                  {user.fullName} ({user.email})
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">Loading audit history...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-sm text-red-600">Failed to load audit history</p>
              </div>
            ) : !auditData?.auditLogs?.length ? (
              <div className="text-center py-8">
                <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No activity history found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {auditData.auditLogs.map((entry: AuditLogEntry) => (
                  <div key={entry.id} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                            {entry.action.includes('VIEW') ? (
                              <EyeIcon className="h-4 w-4 text-gray-600" />
                            ) : (
                              <UserIcon className="h-4 w-4 text-gray-600" />
                            )}
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(entry.action)}`}>
                              {ACTION_LABELS[entry.action] || entry.action}
                            </span>
                          </div>
                          <p className="text-sm text-gray-900 mt-1">
                            Performed by: {entry.adminUser.firstName} {entry.adminUser.lastName}
                            <span className="text-gray-500"> ({entry.adminUser.email})</span>
                          </p>
                          {renderActionDetails(entry)}
                          <div className="flex items-center text-xs text-gray-400 mt-2 space-x-4">
                            <span>{formatDateTime(entry.createdAt)}</span>
                            {entry.ipAddress && (
                              <span>IP: {entry.ipAddress}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {auditData?.pagination && auditData.pagination.pages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-gray-500">
                Page {auditData.pagination.page} of {auditData.pagination.pages}{' '}
                ({auditData.pagination.total} total entries)
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage <= 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, auditData.pagination.pages))}
                  disabled={currentPage >= auditData.pagination.pages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Close Button */}
          <div className="flex justify-end pt-4 border-t mt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserAuditModal
